import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Activity,
  User,
  Hourglass,
  Handshake,
  Wallet,
  CheckCircle2,
  Ban,
  XCircle,
  type LucideIcon,
} from 'lucide-react';
import { LeadStatus } from '../../types';
import { LEAD_STATUS_DISPLAY_ORDER, LEAD_STATUS_I18N_KEYS } from '../../utils/statusUtils';
import Card, { CardHeader, CardTitle, CardContent, CardFooter } from '../ui/Card';
import { useI18n } from '../../context/I18nContext';
import { isDemoMode } from '../../services/dataScope';
import { neutralColors } from '../../styles/colors';
import { STAGE_BAR_VISUAL, STAGE_ICON_VISUAL } from '../../styles/stageVisual';
import InfoTooltip from '../ui/InfoTooltip';
import Tooltip from '../ui/Tooltip';

const STAGE_ICONS: Record<LeadStatus, LucideIcon> = {
  to_contact: User,
  pending: Hourglass,
  negotiation: Handshake,
  in_production: Wallet,
  completed: CheckCircle2,
  abandoned: Ban,
  rejected: XCircle,
};

interface PipelineOverviewProps {
  leadsByStatus: Record<LeadStatus, number>;
  conversionRate: number;
  period?: 'month' | 'year' | 'total';
  className?: string;
  minHeightClass?: string;
  showConversionRate?: boolean;
  showTitle?: boolean;
  showTitleIcon?: boolean;
  showFooter?: boolean;
  /** When set with onStageClick, enables toggle + active row styling (Leads page). */
  activeStatus?: LeadStatus | '';
  onStageClick?: (status: LeadStatus) => void;
}

const PipelineOverview: React.FC<PipelineOverviewProps> = ({
  leadsByStatus,
  conversionRate,
  className = '',
  minHeightClass = 'min-h-[12rem]',
  showConversionRate = true,
  showTitle = true,
  showTitleIcon = true,
  showFooter = false,
  activeStatus = '',
  onStageClick,
}) => {
  const { t } = useI18n();
  const navigate = useNavigate();
  const interactive = !isDemoMode();

  const handleStageClick = (status: LeadStatus) => {
    if (onStageClick) {
      onStageClick(status);
      return;
    }
    navigate(`/app/leads?status=${status}`);
  };

  const stages = LEAD_STATUS_DISPLAY_ORDER.map((status) => ({
    status,
    label: t(LEAD_STATUS_I18N_KEYS[status]),
    count: leadsByStatus[status] || 0,
  }));

  const totalLeads = stages.reduce((sum, stage) => sum + stage.count, 0);

  return (
    <Card className={`h-full ${className}`}>
      {showTitle || showConversionRate ? (
        <CardHeader className="!border-b-0 !pb-1 !mb-1 xl:!pb-2 xl:!mb-2">
          <div
            className={`flex items-center gap-3 ${
              showTitle && showConversionRate ? 'justify-between' : showConversionRate ? 'justify-end' : ''
            }`}
          >
            {showTitle ? (
              <CardTitle className="flex items-center gap-2 text-base font-semibold text-gray-900">
                {showTitleIcon ? (
                  <Activity className="h-5 w-5 shrink-0 text-orange-600" />
                ) : null}
                <span>{t('dashboard.commercialPipeline')}</span>
              </CardTitle>
            ) : null}
            {showConversionRate ? (
              <div className="flex shrink-0 items-center gap-1.5">
                <span className={`hidden text-sm sm:inline ${neutralColors.grayTextLight}`}>
                  {t('dashboard.conversionRate')}
                </span>
                <span className="text-sm font-semibold text-primary-600 tabular-nums">
                  {conversionRate.toFixed(0)}&nbsp;%
                </span>
                <InfoTooltip content={t('dashboard.conversionRateTooltip')} size="sm" />
              </div>
            ) : null}
          </div>
        </CardHeader>
      ) : null}

      <CardContent className={`flex min-h-0 flex-1 flex-col ${minHeightClass}`}>
        <div className="flex min-h-0 flex-1 flex-col justify-evenly gap-1">
          {stages.map((stage) => {
            const iconVisual = STAGE_ICON_VISUAL[stage.status];
            const Icon = STAGE_ICONS[stage.status];
            const bar = STAGE_BAR_VISUAL[stage.status];
            const pct = totalLeads > 0 ? Math.round((stage.count / totalLeads) * 100) : 0;
            const barWidth =
              totalLeads > 0 && stage.count > 0
                ? `${Math.max((stage.count / totalLeads) * 100, 2)}%`
                : '0%';

            const isActive = activeStatus === stage.status;

            const row = (
              <div
                className={`flex min-h-0 flex-1 items-center gap-3 rounded-lg px-2 sm:gap-4 sm:px-2.5 ${
                  interactive
                    ? `cursor-pointer transition-colors ${
                        isActive
                          ? 'bg-primary-50 hover:bg-primary-50/90'
                          : 'hover:bg-gray-50/80 active:bg-gray-100/50'
                      }`
                    : ''
                }`}
                onClick={interactive ? () => handleStageClick(stage.status) : undefined}
                aria-pressed={interactive && isActive ? true : undefined}
              >
                <div className="flex min-w-0 shrink-0 items-center gap-2.5 sm:w-[9.5rem] sm:gap-3 md:w-44">
                  <span
                    className={`inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full ${iconVisual.iconWrap}${
                      isActive ? ' ring-2 ring-primary-200/80' : ''
                    }`}
                  >
                    <Icon className={`h-4 w-4 ${iconVisual.icon}`} strokeWidth={2} aria-hidden />
                  </span>
                  <span
                    className={`truncate text-sm font-medium ${
                      isActive ? 'text-primary-800' : 'text-gray-800'
                    }`}
                  >
                    {stage.label}
                  </span>
                </div>

                <div className={`h-2 min-w-0 flex-1 overflow-hidden rounded-full bg-gray-100`}>
                  <div
                    className={`h-full rounded-full ${bar}`}
                    style={{ width: barWidth }}
                  />
                </div>

                <div className="flex w-[4.25rem] shrink-0 items-baseline justify-end gap-1.5 tabular-nums sm:w-[4.75rem]">
                  <span
                    className={`text-sm font-semibold ${isActive ? 'text-primary-700' : 'text-gray-900'}`}
                  >
                    {stage.count}
                  </span>
                  <span className={`text-xs ${neutralColors.grayTextLight}`}>{pct}%</span>
                </div>
              </div>
            );

            if (!interactive) {
              return <React.Fragment key={stage.status}>{row}</React.Fragment>;
            }

            return (
              <Tooltip
                key={stage.status}
                content={
                  isActive
                    ? t('dashboard.pipelineStageTooltipClear', { status: stage.label })
                    : t('dashboard.pipelineStageTooltip', { status: stage.label })
                }
              >
                {row}
              </Tooltip>
            );
          })}
        </div>

        {showFooter ? (
          <CardFooter>
            <div className="flex justify-between text-sm">
              <span className={neutralColors.grayTextLight}>
                {t('common.total')} {t('leads.title').toLowerCase()}
              </span>
              <span className={`font-medium ${neutralColors.grayTextHover}`}>{totalLeads}</span>
            </div>
          </CardFooter>
        ) : null}
      </CardContent>
    </Card>
  );
};

export default PipelineOverview;
